// Single source of truth for per-role access to Dashboard V2 sections/panels.
//
// Used by:
//  - containers/DashboardV2.js -> render only the permitted panels and skip
//    the fetch of the ones that are hidden.
//
// The server enforces the same matrix in controllers/DashboardV2Controller.php
// (panelsForRoles + the action -> panel map). Keep both sides in sync.
//
// Access rule (per business requirement):
//  - Customer                      -> no dashboard at all.
//  - Head of CB (any user holding
//    that role) and users whose ONLY
//    role is Auditor               -> Performa SLA + Top Customers/Scheme only.
//  - every other role              -> all panels.

export const PANEL_KPI = "kpi";
export const PANEL_EXPIRING = "expiring";
export const PANEL_AUDIT_SCHEDULE = "auditSchedule";
export const PANEL_SLA = "sla";
export const PANEL_ORDER_TREND = "orderTrend";
export const PANEL_TOP_ACTIVE = "topActive";

// Every panel, in render order.
export const ALL_PANELS = [
  PANEL_KPI,
  PANEL_EXPIRING,
  PANEL_AUDIT_SCHEDULE,
  PANEL_SLA,
  PANEL_ORDER_TREND,
  PANEL_TOP_ACTIVE,
];

// Restricted set shared by Head of CB and Auditor-only accounts.
const LIMITED_PANELS = [PANEL_SLA, PANEL_TOP_ACTIVE];

// Normalize the role value coming from the store (array of role names).
function normalizeRoles(roles) {
  if (Array.isArray(roles)) return roles;
  if (roles == null) return [];
  return [roles];
}

// Panels the given role(s) may see. Empty array = dashboard not available.
export function getDashboardPanels(roles) {
  const list = normalizeRoles(roles);

  // Customer is blocked outright, even when combined with another role —
  // this preserves the pre-existing behaviour.
  if (list.includes("Customer")) return [];

  // Holding Head of CB limits the account, regardless of any other role.
  if (list.includes("Head of CB")) return LIMITED_PANELS;

  // Auditor is limited only when it is the account's sole role; an Auditor
  // who also holds another role falls through to full access.
  if (list.length === 1 && list[0] === "Auditor") return LIMITED_PANELS;

  return ALL_PANELS;
}

// Whether the role(s) may see a given panel id.
export function hasPanelAccess(roles, panelId) {
  return getDashboardPanels(roles).includes(panelId);
}
